import { signalStoreFeature, withMethods, withState } from '@ngrx/signals';
import { ELoadingEntity, IErrorProperty } from 'src/models/generic-store.model';
import { baseState, ILoadingErrorState } from 'src/models/common.model';

/**
 * Utility functions to create state updates for loading/error that can be used in updateState calls.
 * These functions take the current state and return partial state updates that can be spread into updateState.
 */
export function withLoading(loadingEntity: ELoadingEntity, currentLoadingProps: ELoadingEntity[]): Partial<ILoadingErrorState> {
  // Convert array to Set to avoid duplicates, add entity, convert back to array
  const set = new Set(currentLoadingProps);
  set.add(loadingEntity);
  return { loadingProps: Array.from(set)}
}

export function withoutLoading(loadingEntity: ELoadingEntity, currentLoadingProps: ELoadingEntity[]): Partial<ILoadingErrorState> {
  // Convert array to Set to avoid duplicates, remove entity, convert back to array
  const set = new Set(currentLoadingProps);
  set.delete(loadingEntity);
  return { loadingProps: Array.from(set) };
}

export function withoutAllLoading(): Partial<ILoadingErrorState> {
  return { loadingProps: [] };
}

export function withError(
  errorEntity: ELoadingEntity,
  errorProperty: IErrorProperty,
  currentErrorProps: Partial<Record<ELoadingEntity, IErrorProperty>>
): Partial<ILoadingErrorState> {
  // Create new object with existing errorProps and add/update the specific entity error
  return { errorProps: { ...currentErrorProps, [errorEntity]: errorProperty } };
}

export function withoutError(
  errorEntity: ELoadingEntity,
  currentErrorProps: Partial<Record<ELoadingEntity, IErrorProperty>>
): Partial<ILoadingErrorState> {
  // Create new object without the specified entity error
  const { [errorEntity]: removed, ...rest } = currentErrorProps;
  return { errorProps: rest };
}

export function withoutAllErrors(): Partial<ILoadingErrorState> {
  return { errorProps: {} };
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function withRequestStatus() {
  return signalStoreFeature(
    withState(baseState),
    withMethods((store) => ({
      // Helper method to check if a specific entity is loading
      isLoading: (loadingEntity: ELoadingEntity): boolean => {
        return store.loadingProps().includes(loadingEntity);
      },
      // Helper method to check if a specific entity has errored
      hasErrored: (loadingEntity: ELoadingEntity): boolean => {
        const errorProps = store.errorProps();
        return !!errorProps[loadingEntity];
      },
      // Helper method to get error for a specific entity
      getError: (loadingEntity: ELoadingEntity): IErrorProperty | null => {
        const errorProps = store.errorProps();
        return errorProps[loadingEntity] ?? null;
      },
    }))
  );
}

